import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Truck, Weight, Fuel } from "lucide-react";

export default function ShippingConfig() {
  const [ranges, setRanges] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [fuelPct, setFuelPct] = useState("17.5");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/settings/shipping").then((r) => { setRanges(r.data.weight_ranges || []); setCarriers(r.data.carriers || []); setFuelPct(String((((r.data.fuel_pct ?? 0.175)) * 100).toFixed(1))); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const upR = (i, k, v) => setRanges((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const upC = (i, k, v) => setCarriers((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const addR = () => setRanges((rs) => [...rs, { key: "", label: "", max_kg: "" }]);
  const addC = () => setCarriers((cs) => [...cs, { id: "", name: "", mult: 1, eta: "", logo: "" }]);
  const delR = (i) => setRanges((rs) => rs.filter((_, j) => j !== i));
  const delC = (i) => setCarriers((cs) => cs.filter((_, j) => j !== i));

  const save = async () => {
    setLoading(true);
    try {
      const wr = ranges.filter((r) => r.key).map((r) => ({ key: String(r.key), label: r.label || `${r.max_kg} kg`, max_kg: parseFloat(r.max_kg) || 0 }));
      const cr = carriers.filter((c) => c.id).map((c) => ({ id: c.id, name: c.name || c.id, mult: parseFloat(c.mult) || 1, eta: c.eta || "", logo: c.logo || "" }));
      await api.put("/settings/shipping", { weight_ranges: wr, carriers: cr, fuel_pct: (parseFloat(fuelPct) || 0) / 100 });
      toast.success("Configurazione spedizioni salvata");
      load();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="shipping-config-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Amministrazione</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Config Spedizioni</h1>
        <p className="text-sm text-muted-foreground mt-1">Fasce di peso e corrieri proposti in fase di prenotazione.</p>
      </div>

      <Card className="p-6 glow-border space-y-3">
        <div className="flex items-center gap-2"><Fuel className="w-4 h-4 text-primary" /><h2 className="font-head font-semibold">Supplemento carburante</h2></div>
        <p className="text-xs text-muted-foreground">Percentuale applicata al costo del corriere, prima di margine e IVA. Cresce automaticamente con il costo del pacco.</p>
        <div className="flex items-center gap-2 max-w-[220px]">
          <Input type="number" step="0.1" value={fuelPct} onChange={(e) => setFuelPct(e.target.value)} data-testid="fuel-pct-input" />
          <span className="text-sm text-muted-foreground font-medium">%</span>
        </div>
      </Card>

      <Card className="p-6 glow-border space-y-3">
        <div className="flex items-center gap-2"><Weight className="w-4 h-4 text-primary" /><h2 className="font-head font-semibold">Fasce di peso</h2></div>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_2fr_1fr_auto] gap-2 text-xs text-muted-foreground px-1"><span>Chiave</span><span>Etichetta</span><span>Max kg</span><span /></div>
          {ranges.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_1fr_auto] gap-2" data-testid={`range-row-${i}`}>
              <Input value={r.key} onChange={(e) => upR(i, "key", e.target.value)} placeholder="1" />
              <Input value={r.label} onChange={(e) => upR(i, "label", e.target.value)} placeholder="Fino a 1 kg" />
              <Input type="number" step="0.1" value={r.max_kg} onChange={(e) => upR(i, "max_kg", e.target.value)} placeholder="1" />
              <Button variant="ghost" size="icon" onClick={() => delR(i)} data-testid={`range-del-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="gap-1" onClick={addR} data-testid="add-range"><Plus className="w-4 h-4" /> Aggiungi fascia</Button>
      </Card>

      <Card className="p-6 glow-border space-y-3">
        <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /><h2 className="font-head font-semibold">Corrieri</h2></div>
        <p className="text-xs text-muted-foreground">Il moltiplicatore regola il prezzo rispetto alla tariffa base (1.0 = base).</p>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_1.4fr_0.7fr_0.9fr_1.6fr_auto] gap-2 text-xs text-muted-foreground px-1"><span>ID</span><span>Nome</span><span>Moltipl.</span><span>Consegna</span><span>Logo (URL)</span><span /></div>
          {carriers.map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_1.4fr_0.7fr_0.9fr_1.6fr_auto] gap-2 items-center" data-testid={`carrier-row-${i}`}>
              <Input value={c.id} onChange={(e) => upC(i, "id", e.target.value)} placeholder="brt" />
              <Input value={c.name} onChange={(e) => upC(i, "name", e.target.value)} placeholder="BRT" />
              <Input type="number" step="0.01" value={c.mult} onChange={(e) => upC(i, "mult", e.target.value)} placeholder="1.0" />
              <Input value={c.eta} onChange={(e) => upC(i, "eta", e.target.value)} placeholder="24-48h" />
              <Input value={c.logo || ""} onChange={(e) => upC(i, "logo", e.target.value)} placeholder="https://…/logo.png" />
              <Button variant="ghost" size="icon" onClick={() => delC(i)} data-testid={`carrier-del-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="gap-1" onClick={addC} data-testid="add-carrier"><Plus className="w-4 h-4" /> Aggiungi corriere</Button>
      </Card>

      <Button onClick={save} disabled={loading} data-testid="save-shipping-config">{loading ? "Salvataggio…" : "Salva configurazione"}</Button>
    </div>
  );
}
